import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useRef } from "react";
import type { FieldValues } from "react-hook-form";
import type { ServiceCharge } from "../api";
import {
  pickServiceChargeBreakdownInputSnapshot,
  type ServiceChargeBreakdownInputSnapshot,
} from "../breakdownInputSnapshots";

type UseServiceChargeBreakdownStalenessOptions = {
  /** Live-watched service charge value from the form. */
  serviceCharge: FieldValues | undefined | null;
  planStableId: string | undefined;
  serviceChargeBreakdownRows: ServiceCharge[] | null;
  onStaleChange?: (planId: string, stale: boolean) => void;
};

type UseServiceChargeBreakdownStalenessReturn = {
  /**
   * Call immediately after a successful service-charge "Generate Breakdown".
   * Seeds the snapshot so the new breakdown is not immediately stale.
   */
  onBreakdownSuccess: (currentServiceCharge: FieldValues) => void;
  /** Call when the service-charge section is removed. */
  onBreakdownCleared: () => void;
};

export function useServiceChargeBreakdownStaleness(
  options: UseServiceChargeBreakdownStalenessOptions,
): UseServiceChargeBreakdownStalenessReturn {
  const { serviceCharge, planStableId, serviceChargeBreakdownRows, onStaleChange } = options;

  const inputSnapshotRef = useRef<ServiceChargeBreakdownInputSnapshot | null>(null);

  const serializedServiceChargeSnapshot = useMemo(
    () => JSON.stringify(pickServiceChargeBreakdownInputSnapshot(serviceCharge)),
    [serviceCharge],
  );

  useEffect(() => {
    if (!serviceChargeBreakdownRows?.length) {
      inputSnapshotRef.current = null;
      if (planStableId) onStaleChange?.(planStableId, false);
      return;
    }
    // First render after server-loaded rows: seed snapshots so we are not immediately stale.
    if (inputSnapshotRef.current === null) {
      inputSnapshotRef.current = pickServiceChargeBreakdownInputSnapshot(serviceCharge);
      if (planStableId) onStaleChange?.(planStableId, false);
      return;
    }
    if (planStableId) {
      onStaleChange?.(
        planStableId,
        !isEqual(pickServiceChargeBreakdownInputSnapshot(serviceCharge), inputSnapshotRef.current),
      );
    }
  }, [serializedServiceChargeSnapshot, serviceChargeBreakdownRows?.length, planStableId, onStaleChange, serviceCharge]);

  const onBreakdownSuccess = (currentServiceCharge: FieldValues) => {
    inputSnapshotRef.current = pickServiceChargeBreakdownInputSnapshot(currentServiceCharge);
    if (planStableId) onStaleChange?.(planStableId, false);
  };

  const onBreakdownCleared = () => {
    inputSnapshotRef.current = null;
    if (planStableId) onStaleChange?.(planStableId, false);
  };

  return { onBreakdownSuccess, onBreakdownCleared };
}
